import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { RefreshCw, Loader2, Merge, BrainCircuit, Network, Trash2, Activity, Database, Server, Clock, CheckCircle2, XCircle, GitMerge, GitBranch, Cpu } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

// ── Types ──

type IntelAction =
	| "deduplicate-skills"
	| "deduplicate-experiences"
	| "deduplicate-skills-llm"
	| "deduplicate-experiences-llm"
	| "build-clusters"
	| "assign-skills-to-clusters"
	| "assign-experiences-to-clusters";

interface BatchLog {
	id: string;
	created_at: string;
	terminated_at: string | null;
	status: string | null;
	topic: string | null;
	details: string | null;
}

interface HealthStatus {
	status: string;
	loaded: boolean;
	lastSync: string | null;
	stats: {
		skills: number;
		experiences: number;
		candidates: number;
		jobs: number;
		organizations: number;
		topicClusters: number;
	};
}

interface DuplicateSkillCandidate {
	skill_a_id: string;
	skill_a_name: string;
	skill_b_id: string;
	skill_b_name: string;
	skill_type: string;
	similarity: number;
	usage_count_a: number;
	usage_count_b: number;
}

interface ParentChildCandidate {
	child_id: string;
	child_name: string;
	parent_id: string;
	parent_name: string;
	skill_type: string;
}

interface SkillHierarchyEntry {
	parent_id: string;
	parent_name: string;
	skill_type: string;
	child_id: string;
	child_name: string;
	usage_count: number;
}

// ── Action definitions ──

const ACTION_GROUPS: Array<{
	titleKey: string;
	icon: React.ElementType;
	actions: Array<{ action: IntelAction; labelKey: string }>;
}> = [
	{
		titleKey: "admin.intelligence.groups.deduplication",
		icon: Merge,
		actions: [
			{ action: "deduplicate-skills", labelKey: "admin.intelligence.actions.deduplicateSkills" },
			{ action: "deduplicate-experiences", labelKey: "admin.intelligence.actions.deduplicateExperiences" },
			{ action: "deduplicate-skills-llm", labelKey: "admin.intelligence.actions.deduplicateSkillsLlm" },
			{ action: "deduplicate-experiences-llm", labelKey: "admin.intelligence.actions.deduplicateExperiencesLlm" },
		],
	},
	{
		titleKey: "admin.intelligence.groups.clustering",
		icon: Network,
		actions: [
			{ action: "build-clusters", labelKey: "admin.intelligence.actions.buildClusters" },
			{ action: "assign-skills-to-clusters", labelKey: "admin.intelligence.actions.assignSkillsToClusters" },
			{ action: "assign-experiences-to-clusters", labelKey: "admin.intelligence.actions.assignExperiencesToClusters" },
		],
	},
];

// ── Helpers ──

function statusBadge(status: string | null) {
	if (!status) return <Badge variant="outline">—</Badge>;
	if (status === "running")
		return <Badge className="bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-100">{status}</Badge>;
	if (status === "done")
		return <Badge className="bg-green-100 text-green-800 border-green-300 hover:bg-green-100">{status}</Badge>;
	if (status === "error")
		return <Badge variant="destructive">{status}</Badge>;
	return <Badge variant="outline">{status}</Badge>;
}

function formatDate(iso: string | null) {
	if (!iso) return "—";
	return new Date(iso).toLocaleString();
}

// ── Component ──

export function IntelligenceActionsTab() {
	const { t } = useTranslation();
	const { toast } = useToast();
	const [triggering, setTriggering] = useState<Set<IntelAction>>(new Set());
	const [logs, setLogs] = useState<BatchLog[]>([]);
	const [logsLoading, setLogsLoading] = useState(false);
	const [deletingLog, setDeletingLog] = useState<Set<string>>(new Set());
	const [healthStatus, setHealthStatus] = useState<HealthStatus | null>(null);
	const [isLoadingHealth, setIsLoadingHealth] = useState(true);
	const [isSyncing, setIsSyncing] = useState(false);
	const [isRecomputingCentroids, setIsRecomputingCentroids] = useState(false);
	
	// Concept maintenance state
	const [duplicatesDialog, setDuplicatesDialog] = useState(false);
	const [duplicates, setDuplicates] = useState<DuplicateSkillCandidate[]>([]);
	const [loadingDuplicates, setLoadingDuplicates] = useState(false);
	
	const [parentChildDialog, setParentChildDialog] = useState(false);
	const [parentChildCandidates, setParentChildCandidates] = useState<ParentChildCandidate[]>([]);
	const [loadingParentChild, setLoadingParentChild] = useState(false);
	
	const [hierarchyDialog, setHierarchyDialog] = useState(false);
	const [hierarchy, setHierarchy] = useState<SkillHierarchyEntry[]>([]);
	const [loadingHierarchy, setLoadingHierarchy] = useState(false);

	const fetchHealthStatus = useCallback(async () => {
		try {
			const { data, error } = await supabase.functions.invoke('digger-intelligence', {
				method: 'GET',
			});

			if (error) throw error;

			if (data?.status) {
				setHealthStatus(data as HealthStatus);
			}
		} catch (error: any) {
			console.error("Failed to fetch health status:", error);
		} finally {
			setIsLoadingHealth(false);
		}
	}, []);

	const fetchLogs = useCallback(async () => {
		setLogsLoading(true);
		try {
			const { data, error } = await supabase
				.from("digger-intelligence-batch-processing" as any)
				.select("id, created_at, terminated_at, status, topic, details")
				.order("created_at", { ascending: false })
				.limit(50);

			if (error) throw error;
			setLogs((data as BatchLog[]) ?? []);
		} catch (err: any) {
			console.error("Failed to fetch batch logs:", err);
		} finally {
			setLogsLoading(false);
		}
	}, []);

	// Initial load
	useEffect(() => {
		fetchLogs();
		fetchHealthStatus();
	}, [fetchLogs, fetchHealthStatus]);

	// Auto-poll every 3s while any task is running, also refresh health status
	useEffect(() => {
		const hasRunning = logs.some(l => l.status === 'running');
		if (!hasRunning) return;

		const interval = setInterval(() => {
			fetchLogs();
			fetchHealthStatus();
		}, 3000);
		return () => clearInterval(interval);
	}, [logs, fetchLogs, fetchHealthStatus]);

	async function callSyncEndpoint(command: string, operationName: string) {
		setIsSyncing(true);
		try {
			const { data, error } = await supabase.functions.invoke(`digger-intelligence?command=${command}`, {
				method: 'POST',
			});

			if (error) throw error;

			toast({
				title: t("admin.intelligence.toast.accepted"),
				description: `${operationName} ${t("admin.intelligence.toast.completed")}`,
			});

			// Refresh health status
			await fetchHealthStatus();
		} catch (error: any) {
			toast({
				title: t("admin.intelligence.toast.error"),
				description: error.message || `Failed to ${operationName}`,
				variant: "destructive",
			});
		} finally {
			setIsSyncing(false);
		}
	}

	async function recomputeCentroids() {
		setIsRecomputingCentroids(true);
		try {
			const { data, error } = await supabase.functions.invoke('digger-intelligence?command=recompute-centroids', {
				method: 'POST',
			});

			if (error) throw error;

			toast({
				title: "Centroids recomputed",
				description: data?.message || "All candidate and job centroids have been updated.",
			});

			await fetchHealthStatus();
		} catch (error: any) {
			toast({
				title: "Error",
				description: error.message || "Failed to recompute centroids",
				variant: "destructive",
			});
		} finally {
			setIsRecomputingCentroids(false);
		}
	}

	async function triggerAction(action: IntelAction) {
		setTriggering(prev => new Set(prev).add(action));
		try {
			const { data, error } = await supabase.functions.invoke(
				"digger-intelligence?command=trigger-intel-action",
				{ method: "POST", body: { action } },
			);

			if (error) throw error;

			if (data?.status === "accepted") {
				toast({
					title: t("admin.intelligence.toast.accepted"),
					description: t("admin.intelligence.toast.acceptedDesc", { action }),
				});
				// Refresh logs after a short delay to pick up the new entry
				setTimeout(fetchLogs, 1500);
			} else if (data?.error) {
				toast({
					title: t("admin.intelligence.toast.error"),
					description: data.error,
					variant: "destructive",
				});
			}
		} catch (err: any) {
			toast({
				title: t("admin.intelligence.toast.error"),
				description: err.message ?? String(err),
				variant: "destructive",
			});
		} finally {
			setTriggering(prev => {
				const next = new Set(prev);
				next.delete(action);
				return next;
			});
		}
	}

	async function deleteLog(id: string) {
		setDeletingLog(prev => new Set(prev).add(id));
		try {
			const { error } = await supabase
				.from("digger-intelligence-batch-processing" as any)
				.delete()
				.eq("id", id);

			if (error) throw error;
			setLogs(prev => prev.filter(l => l.id !== id));
		} catch (err: any) {
			toast({
				title: t("admin.intelligence.toast.error"),
				description: err.message ?? String(err),
				variant: "destructive",
			});
		} finally {
			setDeletingLog(prev => {
				const next = new Set(prev);
				next.delete(id);
				return next;
			});
		}
	}

	async function loadDuplicates() {
		setLoadingDuplicates(true);
		try {
			const { data, error } = await supabase.functions.invoke('digger-intelligence?command=find-duplicate-skills', {
				method: 'GET',
			});

			if (error) throw error;
			setDuplicates(data?.candidates || []);
			setDuplicatesDialog(true);
		} catch (error: any) {
			toast({
				title: "Error",
				description: error.message || "Failed to load duplicate skills",
				variant: "destructive",
			});
		} finally {
			setLoadingDuplicates(false);
		}
	}

	async function loadParentChildCandidates() {
		setLoadingParentChild(true);
		try {
			const { data, error } = await supabase.functions.invoke('digger-intelligence?command=find-parent-child-candidates', {
				method: 'GET',
			});

			if (error) throw error;
			setParentChildCandidates(data?.candidates || []);
			setParentChildDialog(true);
		} catch (error: any) {
			toast({
				title: "Error",
				description: error.message || "Failed to load parent-child candidates",
				variant: "destructive",
			});
		} finally {
			setLoadingParentChild(false);
		}
	}

	async function loadHierarchy() {
		setLoadingHierarchy(true);
		try {
			const { data, error } = await supabase.functions.invoke('digger-intelligence?command=show-skill-hierarchy', {
				method: 'GET',
			});

			if (error) throw error;
			setHierarchy(data?.hierarchy || []);
			setHierarchyDialog(true);
		} catch (error: any) {
			toast({
				title: "Error",
				description: error.message || "Failed to load skill hierarchy",
				variant: "destructive",
			});
		} finally {
			setLoadingHierarchy(false);
		}
	}

	async function mergeSkills(sourceId: string, targetId: string, sourceName: string, targetName: string) {
		if (!confirm(`Merge "${sourceName}" into "${targetName}"?\n\nThis will:\n- Update all references\n- Delete the source skill\n- Reload Fast-Vector-Service\n\nThis cannot be undone.`)) {
			return;
		}

		try {
			const { data, error } = await supabase.functions.invoke('digger-intelligence?command=merge-skill-concepts', {
				method: 'POST',
				body: { sourceSkillId: sourceId, targetSkillId: targetId },
			});

			if (error) throw error;

			toast({
				title: "Success",
				description: data?.message || "Skills merged successfully",
			});

			// Refresh duplicates list
			await loadDuplicates();
			await fetchHealthStatus();
		} catch (error: any) {
			toast({
				title: "Error",
				description: error.message || "Failed to merge skills",
				variant: "destructive",
			});
		}
	}

	async function setParent(childId: string, parentId: string, childName: string, parentName: string) {
		if (!confirm(`Set "${childName}" as a child of "${parentName}"?\n\nThis will reload Fast-Vector-Service.\n\nThis can be undone by manually updating the database.`)) {
			return;
		}

		try {
			const { data, error } = await supabase.functions.invoke('digger-intelligence?command=set-parent-concept', {
				method: 'POST',
				body: { childSkillId: childId, parentSkillId: parentId },
			});

			if (error) throw error;

			toast({
				title: "Success",
				description: data?.message || "Parent-child relationship established",
			});

			// Refresh lists
			await loadParentChildCandidates();
			await loadHierarchy();
			await fetchHealthStatus();
		} catch (error: any) {
			toast({
				title: "Error",
				description: error.message || "Failed to set parent concept",
				variant: "destructive",
			});
		}
	}

	return (
		<div className="space-y-6">
			{/* ── Health Status ── */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<Activity className="h-5 w-5" />
						{t("admin.intelligence.health.title", "Service Health")}
					</CardTitle>
					<CardDescription>{t("admin.intelligence.health.description", "Fast-Vector-Service status and statistics")}</CardDescription>
				</CardHeader>
				<CardContent>
					{isLoadingHealth ? (
						<div className="flex justify-center py-8">
							<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
						</div>
					) : healthStatus ? (
						<div className="space-y-4">
							<div className="flex items-center gap-4">
								<div className="flex items-center gap-2">
									<div className={`h-3 w-3 rounded-full ${healthStatus.status === 'ok' ? 'bg-green-500' : 'bg-red-500'}`} />
									<span className="font-medium">{healthStatus.status === 'ok' ? t("admin.intelligence.health.operational", "Operational") : t("admin.intelligence.health.error", "Error")}</span>
								</div>
								<div className="flex items-center gap-2 text-sm text-muted-foreground">
									<Clock className="h-4 w-4" />
									{t("admin.intelligence.health.lastSync", "Last sync")}: {healthStatus.lastSync ? new Date(healthStatus.lastSync).toLocaleString() : t("admin.intelligence.health.never", "Never")}
								</div>
							</div>

							<div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
								<div className="border rounded-lg p-3">
									<div className="text-sm text-muted-foreground">{t("admin.intelligence.health.skills", "Skills")}</div>
									<div className="text-2xl font-bold">{healthStatus.stats.skills.toLocaleString()}</div>
								</div>
								<div className="border rounded-lg p-3">
									<div className="text-sm text-muted-foreground">{t("admin.intelligence.health.experiences", "Experiences")}</div>
									<div className="text-2xl font-bold">{healthStatus.stats.experiences.toLocaleString()}</div>
								</div>
								<div className="border rounded-lg p-3">
									<div className="text-sm text-muted-foreground">{t("admin.intelligence.health.candidates", "Candidates")}</div>
									<div className="text-2xl font-bold">{healthStatus.stats.candidates.toLocaleString()}</div>
								</div>
								<div className="border rounded-lg p-3">
									<div className="text-sm text-muted-foreground">{t("admin.intelligence.health.jobs", "Jobs")}</div>
									<div className="text-2xl font-bold">{healthStatus.stats.jobs.toLocaleString()}</div>
								</div>
								<div className="border rounded-lg p-3">
									<div className="text-sm text-muted-foreground">{t("admin.intelligence.health.organizations", "Organizations")}</div>
									<div className="text-2xl font-bold">{healthStatus.stats.organizations.toLocaleString()}</div>
								</div>
								<div className="border rounded-lg p-3">
									<div className="text-sm text-muted-foreground">{t("admin.intelligence.health.topicClusters", "Topic Clusters")}</div>
									<div className="text-2xl font-bold">{healthStatus.stats.topicClusters.toLocaleString()}</div>
								</div>
							</div>
						</div>
					) : (
						<Alert variant="destructive">
							<XCircle className="h-4 w-4" />
							<AlertTitle>{t("admin.intelligence.health.unavailable", "Service Unavailable")}</AlertTitle>
							<AlertDescription>
								{t("admin.intelligence.health.unavailableDesc", "Unable to connect to Fast-Vector-Service")}
							</AlertDescription>
						</Alert>
					)}
				</CardContent>
			</Card>

			{/* ── Sync Operations ── */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<Database className="h-5 w-5" />
						{t("admin.intelligence.sync.title", "Data Synchronization")}
					</CardTitle>
					<CardDescription>{t("admin.intelligence.sync.description", "Sync data from Supabase to the Fast-Vector-Service")}</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="flex gap-3">
						<Button
							onClick={() => callSyncEndpoint('sync', t("admin.intelligence.sync.incremental", "Incremental Sync"))}
							disabled={isSyncing}
							variant="outline"
						>
							{isSyncing ? (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							) : (
								<RefreshCw className="mr-2 h-4 w-4" />
							)}
							{t("admin.intelligence.sync.incremental", "Incremental Sync")}
						</Button>
						<Button
							onClick={() => callSyncEndpoint('full-reload', t("admin.intelligence.sync.fullReload", "Full Reload"))}
							disabled={isSyncing}
							variant="outline"
						>
							{isSyncing ? (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							) : (
								<Server className="mr-2 h-4 w-4" />
							)}
							{t("admin.intelligence.sync.fullReload", "Full Reload")}
						</Button>
						<Button
							onClick={recomputeCentroids}
							disabled={isRecomputingCentroids}
							variant="outline"
						>
							{isRecomputingCentroids ? (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							) : (
								<Cpu className="mr-2 h-4 w-4" />
							)}
							Recompute Centroids
						</Button>
					</div>
				</CardContent>
			</Card>

			{/* ── Action groups ── */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<BrainCircuit className="h-5 w-5" />
						{t("admin.intelligence.title")}
					</CardTitle>
					<CardDescription>{t("admin.intelligence.description")}</CardDescription>
				</CardHeader>
				<CardContent className="space-y-6">
					{ACTION_GROUPS.map(group => {
						const GroupIcon = group.icon;
						return (
							<div key={group.titleKey}>
								<h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2 mb-3">
									<GroupIcon className="h-4 w-4" />
									{t(group.titleKey)}
								</h3>
								<div className="flex flex-wrap gap-2">
									{group.actions.map(({ action, labelKey }) => {
										const isRunning = triggering.has(action);
										return (
											<Button
												key={action}
												variant="secondary"
												size="sm"
												disabled={isRunning}
												onClick={() => triggerAction(action)}
												className="gap-2"
											>
												{isRunning
													? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
													: <GroupIcon className="h-3.5 w-3.5 shrink-0 opacity-70" />
												}
												{t(labelKey)}
											</Button>
										);
									})}
								</div>
							</div>
						);
					})}
				</CardContent>
			</Card>

			{/* ── Concept Maintenance (Phase 6) ── */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<GitMerge className="h-5 w-5" />
						Concept Maintenance
					</CardTitle>
					<CardDescription>Manage skill concept clustering, merging, and parent-child relationships</CardDescription>
				</CardHeader>
				<CardContent className="space-y-3">
					<div className="flex flex-wrap gap-2">
						<Button
							variant="outline"
							size="sm"
							disabled={loadingDuplicates}
							onClick={loadDuplicates}
							className="gap-2"
						>
							{loadingDuplicates ? (
								<Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
							) : (
								<GitMerge className="h-3.5 w-3.5 shrink-0" />
							)}
							Find Duplicate Skills
						</Button>
						<Button
							variant="outline"
							size="sm"
							disabled={loadingParentChild}
							onClick={loadParentChildCandidates}
							className="gap-2"
						>
							{loadingParentChild ? (
								<Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
							) : (
								<GitBranch className="h-3.5 w-3.5 shrink-0" />
							)}
							Find Parent-Child Candidates
						</Button>
						<Button
							variant="outline"
							size="sm"
							disabled={loadingHierarchy}
							onClick={loadHierarchy}
							className="gap-2"
						>
							{loadingHierarchy ? (
								<Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
							) : (
								<Network className="h-3.5 w-3.5 shrink-0" />
							)}
							View Skill Hierarchy
						</Button>
					</div>
				</CardContent>
			</Card>

			{/* ── Batch processing logs ── */}
			<Card>
				<CardHeader>
					<div className="flex items-center justify-between">
						<div>
							<CardTitle>{t("admin.intelligence.logs.title")}</CardTitle>
							<CardDescription>{t("admin.intelligence.logs.description")}</CardDescription>
						</div>
						<Button variant="outline" size="sm" onClick={fetchLogs} disabled={logsLoading}>
							<RefreshCw className={`mr-2 h-4 w-4 ${logsLoading ? "animate-spin" : ""}`} />
							{t("admin.refresh")}
						</Button>
					</div>
				</CardHeader>
				<CardContent>
					{logsLoading && logs.length === 0 ? (
						<div className="flex justify-center py-8">
							<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
						</div>
					) : logs.length === 0 ? (
						<p className="text-sm text-muted-foreground text-center py-8">
							{t("admin.intelligence.logs.empty")}
						</p>
					) : (
						<div className="overflow-x-auto">
							<table className="w-full text-sm">
								<thead>
									<tr className="border-b text-muted-foreground">
										<th className="text-left pb-2 pr-4 font-medium">{t("admin.intelligence.logs.cols.status")}</th>
										<th className="text-left pb-2 pr-4 font-medium">{t("admin.intelligence.logs.cols.topic")}</th>
										<th className="text-left pb-2 pr-4 font-medium">{t("admin.intelligence.logs.cols.details")}</th>
										<th className="text-left pb-2 pr-4 font-medium">{t("admin.intelligence.logs.cols.startedAt")}</th>
										<th className="text-left pb-2 pr-4 font-medium">{t("admin.intelligence.logs.cols.terminatedAt")}</th>
										<th className="pb-2 font-medium"></th>
									</tr>
								</thead>
								<tbody>
									{logs.map(log => (
										<tr key={log.id} className="border-b last:border-0 hover:bg-muted/30">
											<td className="py-2 pr-4">{statusBadge(log.status)}</td>
											<td className="py-2 pr-4 text-xs font-mono whitespace-nowrap">
												{log.topic ?? "—"}
											</td>
											<td className="py-2 pr-4 max-w-xs truncate text-xs text-muted-foreground" title={log.details ?? ""}>
												{log.details ?? "—"}
											</td>
											<td className="py-2 pr-4 text-xs text-muted-foreground whitespace-nowrap">
												{formatDate(log.created_at)}
											</td>
											<td className="py-2 pr-4 text-xs text-muted-foreground whitespace-nowrap">
												{formatDate(log.terminated_at)}
											</td>
											<td className="py-2">
												<Button
													variant="ghost"
													size="icon"
													className="h-7 w-7 text-muted-foreground hover:text-destructive"
													disabled={deletingLog.has(log.id)}
													onClick={() => deleteLog(log.id)}
												>
													{deletingLog.has(log.id)
														? <Loader2 className="h-3.5 w-3.5 animate-spin" />
														: <Trash2 className="h-3.5 w-3.5" />
													}
												</Button>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</CardContent>
			</Card>

			{/* ── Duplicate Skills Dialog ── */}
			<Dialog open={duplicatesDialog} onOpenChange={setDuplicatesDialog}>
				<DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle>Duplicate Skill Candidates</DialogTitle>
						<DialogDescription>
							Skills with high embedding similarity (≥0.95) and same skill_type. Merge to consolidate concepts.
						</DialogDescription>
					</DialogHeader>
					{duplicates.length === 0 ? (
						<p className="text-sm text-muted-foreground text-center py-8">No duplicate candidates found</p>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Skill A</TableHead>
									<TableHead>Skill B</TableHead>
									<TableHead>Type</TableHead>
									<TableHead>Similarity</TableHead>
									<TableHead>Usage A</TableHead>
									<TableHead>Usage B</TableHead>
									<TableHead></TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{duplicates.map((dup, idx) => (
									<TableRow key={idx}>
										<TableCell className="font-mono text-xs">{dup.skill_a_name}</TableCell>
										<TableCell className="font-mono text-xs">{dup.skill_b_name}</TableCell>
										<TableCell><Badge variant="outline">{dup.skill_type}</Badge></TableCell>
										<TableCell>{(dup.similarity * 100).toFixed(1)}%</TableCell>
										<TableCell>{dup.usage_count_a}</TableCell>
										<TableCell>{dup.usage_count_b}</TableCell>
										<TableCell>
											<div className="flex gap-1">
												<Button
													size="sm"
													variant="ghost"
													onClick={() => mergeSkills(dup.skill_a_id, dup.skill_b_id, dup.skill_a_name, dup.skill_b_name)}
												>
													A→B
												</Button>
												<Button
													size="sm"
													variant="ghost"
													onClick={() => mergeSkills(dup.skill_b_id, dup.skill_a_id, dup.skill_b_name, dup.skill_a_name)}
												>
													B→A
												</Button>
											</div>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</DialogContent>
			</Dialog>

			{/* ── Parent-Child Candidates Dialog ── */}
			<Dialog open={parentChildDialog} onOpenChange={setParentChildDialog}>
				<DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle>Parent-Child Relationship Candidates</DialogTitle>
						<DialogDescription>
							Skills where the child name contains the parent name (e.g., "Agile Project Management" → "Project Management")
						</DialogDescription>
					</DialogHeader>
					{parentChildCandidates.length === 0 ? (
						<p className="text-sm text-muted-foreground text-center py-8">No parent-child candidates found</p>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Child</TableHead>
									<TableHead>Parent</TableHead>
									<TableHead>Type</TableHead>
									<TableHead></TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{parentChildCandidates.map((pc, idx) => (
									<TableRow key={idx}>
										<TableCell className="font-mono text-xs">{pc.child_name}</TableCell>
										<TableCell className="font-mono text-xs">{pc.parent_name}</TableCell>
										<TableCell><Badge variant="outline">{pc.skill_type}</Badge></TableCell>
										<TableCell>
											<Button
												size="sm"
												variant="ghost"
												onClick={() => setParent(pc.child_id, pc.parent_id, pc.child_name, pc.parent_name)}
											>
												Set Relationship
											</Button>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</DialogContent>
			</Dialog>

			{/* ── Skill Hierarchy Dialog ── */}
			<Dialog open={hierarchyDialog} onOpenChange={setHierarchyDialog}>
				<DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle>Skill Hierarchy</DialogTitle>
						<DialogDescription>
							Current parent-child skill relationships
						</DialogDescription>
					</DialogHeader>
					{hierarchy.length === 0 ? (
						<p className="text-sm text-muted-foreground text-center py-8">No hierarchies found</p>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Parent</TableHead>
									<TableHead>Child</TableHead>
									<TableHead>Type</TableHead>
									<TableHead>Usage</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{hierarchy.map((h, idx) => (
									<TableRow key={idx}>
										<TableCell className="font-mono text-xs font-bold">{h.parent_name}</TableCell>
										<TableCell className="font-mono text-xs pl-8">↳ {h.child_name}</TableCell>
										<TableCell><Badge variant="outline">{h.skill_type}</Badge></TableCell>
										<TableCell>{h.usage_count}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</DialogContent>
			</Dialog>
		</div>
	);
}
